// P:\0PJ\synforge\frontend\lib\api\meeting.ts v3 2026.09.08
//   v3: meetingmakeAPI.panelistTurn() 추가 - 사용자 패널 참여 발언 제출
//       (백엔드 PanelistTurnView 와 짝).
//   v2: outputImageParamsAPI.setParams 인자 확장(2개 -> 5개) - 품질/비율/해상도/
//       형식/배경. 백엔드 outputimageparams1.views.SetImageParamsView 와 짝을 이룬다.
import { api } from "./core"

export const flowAPI = {
  start: (id: string) => api.post(`/flow/${id}/start/`),
  answers: (id: string, answerText: string) =>
    api.post(`/flow/${id}/answers/`, { answer_text: answerText || "" }),
  proceed: (id: string) => api.post(`/flow/${id}/proceed/`),
  conclude: (id: string) => api.post(`/flow/${id}/conclude/`),
  recommend: (agenda: string, fileContext?: string, modelNames?: string[], handoffId?: string) =>
    api.post("/flow/recommend/", {
      agenda,
      file_context: fileContext || "",
      model_names:  modelNames || [],
      handoff_id:   handoffId || null,
    }),
}

export interface ClipPayload {
  clip_id: number
  file_name: string
  status: "pending" | "duration_wait" | "clipping" | "clip_ready" | "selected" | "failed"
  duration: number
  max_duration: number
  select_duration: number
  clip_url: string
  clip_duration: number
  clip_fps: number
  select_start: number | null
  select_end: number | null
  error_log: string
}

export const clipAPI = {
  start: (conversationId: number, fileName: string, fsRoot: string) =>
    api.post<ClipPayload>("/filestore/clip/", {
      conversation_id: conversationId,
      file_name: fileName,
      fs_root: fsRoot,
    }),
  status: (clipId: number) => api.get<ClipPayload>(`/filestore/clip/${clipId}/`),
  range: (clipId: number, mode: "first" | "range", start?: number, end?: number) =>
    api.post<ClipPayload>(`/filestore/clip/${clipId}/range/`, {
      mode,
      ...(mode === "range" ? { start, end } : {}),
    }),
  select: (clipId: number, start: number) =>
    api.post<ClipPayload>(`/filestore/clip/${clipId}/select/`, { start }),
}

export const sessionAPI = {
  list:            () => api.get("/sessions/"),
  create:          (data: {
    agenda: string
    file_context?: string
    output_type: string
    output_types?: string[]
    round_count: number
    group?: string
    config_ids: string[]
    handoff_id?: string
  }) => api.post("/sessions/", data),
  detail:          (id: string) => api.get(`/sessions/${id}/`),
  start:           (id: string) => api.post(`/sessions/${id}/start/`),
  stop:            (id: string) => api.post(`/sessions/${id}/stop/`),
  status:          (id: string) => api.get(`/sessions/${id}/status/`),
  delete:          (id: string) => api.delete(`/sessions/${id}/`),
  cancel:          (id: string, reason?: string) => api.post(`/sessions/${id}/cancel/`, { reason }),
  recommend:       (agenda: string) => api.post("/sessions/recommend/", { agenda }),
  recommendV2: (agenda: string, fileContext?: string) =>
    api.post("/sessions/recommend-v2/", { agenda, file_context: fileContext || "" }),
  recommendV2Branch: (agenda: string, fileContext?: string, modelNames?: string[]) =>
    api.post("/sessions/recommend-v2-branch/", {
      agenda,
      file_context: fileContext || "",
      model_names: modelNames || [],
    }),
  confirmPanel:         (id: string, excludeConfigIds?: string[], userAnswers?: Record<string, string>, confirmedIntent?: string) =>
    api.post(`/sessions/${id}/confirm-panel/`, {
      exclude_config_ids: excludeConfigIds || [],
      user_answers:       userAnswers || {},
      confirmed_intent:   confirmedIntent || "",
    }),
  continueMeeting:      (id: string) => api.post(`/sessions/${id}/continue/`),
  continueToConclusion: (id: string) => api.post(`/sessions/${id}/continue-conclusion/`),
  submitAnswers:   (id: string, answers: Record<string, string>, confirmedIntent: string, additionalFileContext?: string) =>
    api.post(`/sessions/${id}/submit-answers/`, {
      user_answers:            answers,
      confirmed_intent:        confirmedIntent,
      additional_file_context: additionalFileContext || "",
    }),
  continueToSession2:   (id: string) => api.post(`/sessions/${id}/continue-session2/`),
}

export const meetingmakeAPI = {
  testReset: (handoffId: string) =>
    api.post<{ deleted: number }>("/meetingmake/test-reset/", { handoff_id: handoffId }),
  testOutput: (sessionId: string) =>
    api.post<{ session_id: string }>("/meetingmake/test-output/", { session_id: sessionId }),
  start: (handoffId: string) =>
    api.post<{ session_id: string; started: boolean }>("/meetingmake/start/", {
      handoff_id: handoffId,
    }),
  status: (sessionId: string) =>
    api.get(`/meetingmake/${sessionId}/status/`),
  turns: (sessionId: string) =>
    api.get(`/meetingmake/${sessionId}/turns/`),
  answer: (sessionId: string, answer: string, files: File[] = []) => {
    if (!files.length) {
      return api.post(`/meetingmake/${sessionId}/answer/`, { answer })
    }
    const form = new FormData()
    form.append("answer", answer)
    files.forEach((f) => form.append("files", f))
    return api.post(`/meetingmake/${sessionId}/answer/`, form, {
      headers: { "Content-Type": undefined },
    })
  },
  // 사용자 패널 참여 발언 — answer()와 별개 엔드포인트(UserInput 미사용,
  //   즉시 검증 수행). 파일 유무에 따라 json/multipart 로 나뉘는 패턴은 answer()와 동일.
  panelistTurn: (sessionId: string, answer: string, files: File[] = []) => {
    if (!files.length) {
      return api.post(`/meetingmake/${sessionId}/panelist_turn/`, { answer })
    }
    const form = new FormData()
    form.append("answer", answer)
    files.forEach((f) => form.append("files", f))
    return api.post(`/meetingmake/${sessionId}/panelist_turn/`, form, {
      headers: { "Content-Type": undefined },
    })
  },
  proceed: (sessionId: string) =>
    api.post(`/meetingmake/${sessionId}/proceed/`, {}),
  retry: (sessionId: string) =>
    api.post(`/meetingmake/${sessionId}/retry/`, {}),
  cancel: (sessionId: string, reason = "user_cancelled") =>
    api.post(`/meetingmake/${sessionId}/cancel/`, { reason }),
  panels: (sessionId: string) =>
    api.get(`/meetingmake/${sessionId}/panels/`),
  stream: (sessionId: string, onEvent: (data: any) => void, onError?: () => void) => {
    const controller = new AbortController()
    let alive = true
    let readerRef: ReadableStreamDefaultReader<Uint8Array> | null = null

    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : ""
    const base = api.defaults.baseURL || "http://127.0.0.1:8000/api"

    const run = async () => {
      try {
        const res = await fetch(`${base}/meetingmake/${sessionId}/stream/`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
        if (!res.ok || !res.body) throw new Error(`stream failed: ${res.status}`)

        const reader  = res.body.getReader()
        readerRef = reader
        const decoder = new TextDecoder()
        let buf = ""

        while (alive) {
          const { value, done } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })

          let idx
          while ((idx = buf.indexOf("\n\n")) !== -1) {
            const chunk = buf.slice(0, idx)
            buf = buf.slice(idx + 2)
            const line = chunk.split("\n").find((l) => l.startsWith("data: "))
            if (!line) continue
            try {
              onEvent(JSON.parse(line.slice(6)))
            } catch (e) {
              console.error("[stream] parse failed:", e)
            }
          }
        }
      } catch (e: any) {
        if (e?.name !== "AbortError") {
          console.error("[stream] connection failed:", e)
          onError?.()
        }
      } finally {
        try { readerRef?.releaseLock() } catch {}
      }
    }
    run()

    return () => {
      alive = false
      controller.abort()
      try {
        readerRef?.cancel()?.catch(() => {})
      } catch {}
    }
  },
  excludePanels: (sessionId: string, excluded: string[]) =>
    api.post(`/meetingmake/${sessionId}/panels/`, { excluded }),
  types: () => {
    const locale =
      typeof document !== "undefined"
        ? document.cookie.split("; ").find((r) => r.startsWith("locale="))?.split("=")[1]
        : undefined
    return api.get(`/meetingmake/types/${locale ? `?locale=${locale}` : ""}`)
  },
}

export const outputImageParamsAPI = {
  // 이미지 출력 옵션 — 전부 객관식. 실제 픽셀 크기(1024x1024 등)는 백엔드가
  //   aspect+resolution 조합표로 만든다(프론트는 픽셀값을 모른다).
  setParams: (
    sessionId: string,
    quality: string, aspect: string, resolution: string,
    format: string, background: string,
  ) =>
    api.post(`/outputimageparams1/image-params/${sessionId}/`, {
      quality,
      aspect,
      resolution,
      format,
      background,
    }),
}

export const outputAiAPI = {
  setMethod: (sessionId: string, method: "system" | "ai") =>
    api.post(`/output-ai/output-method/${sessionId}/`, { output_method: method }),
}